<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { ItemDefinition, ItemStatus } from "~/api";
import { showError } from "~/utils/handleAPIError";
import SortableStatuses from "./SortableStatuses.vue";

// @ts-expect-error - error
const localItemDefinition = ref<ItemDefinition>({});

const props = defineProps<{
  itemDefinition: ItemDefinition;
}>();

const newItemStatus = ref<ItemStatus>({
  code: "",
  label: "",
  isEndpoint: false,
});
const isEditing = ref(false);
const selectedStatus = ref<ItemStatus>();

const editStatus = (status: ItemStatus) => {
  isEditing.value = true;
  selectedStatus.value = { ...status };
  newItemStatus.value = { ...status };
};

const saveNeeded = computed(() => {
  return (
    newItemStatus.value.code !== selectedStatus.value?.code ||
    newItemStatus.value.label !== selectedStatus.value.label
  );
});

const resetInputs = () => {
  isEditing.value = false;
  selectedStatus.value = undefined;
  newItemStatus.value = { code: "", label: "", isEndpoint: false };
};

const removeSpaces = () => {
  if (newItemStatus.value.code) {
    newItemStatus.value.code = newItemStatus.value.code.replace(/\s+/g, "");
  }
};

const updateStatus = async () => {
  const { code, label, isEndpoint } = newItemStatus.value;

  if (!label) {
    showError("Label is required");
    return;
  }

  const statusList = localItemDefinition.value.statusList || [];

  const isDuplicateCode = statusList.filter(
    (s) => s.code?.toLowerCase() === code?.toLowerCase(),
  ).length;

  if (isDuplicateCode > 1) {
    showError(`Status with code ${code} already exists`);
    return;
  }

  if (!code && label) {
    newItemStatus.value.code = label.replace(/\s+/g, "");
  }

  if (!selectedStatus.value) {
    if (
      localItemDefinition.value.statusList.find(
        (s) =>
          s.code?.toLowerCase() === newItemStatus.value.code?.toLowerCase(),
      )
    ) {
      showError(
        `Status with code "${newItemStatus.value.code}" already exists`,
      );
      return;
    }

    if (!localItemDefinition.value.statusList) {
      localItemDefinition.value.statusList = [];
    }
    localItemDefinition.value.statusList.push({
      code: newItemStatus.value.code,
      label: label,
      isEndpoint: isEndpoint,
    });
  } else {
    const statusIndex = localItemDefinition.value.statusList?.findIndex(
      (s) => s.code === selectedStatus.value?.code,
    );
    if (statusIndex !== -1) {
      localItemDefinition.value.statusList[statusIndex] = {
        ...newItemStatus.value,
      };
    }
  }
  resetInputs();
};

const deleteStatus = () => {
  const statusList = localItemDefinition.value.statusList;

  const statusIndex = statusList.findIndex(
    (s) => s.code === selectedStatus.value?.code,
  );

  if (statusIndex !== -1) {
    statusList.splice(statusIndex, 1);
    localItemDefinition.value.statusList = statusList;
    resetInputs();
  }
};

onMounted(() => {
  localItemDefinition.value = props.itemDefinition;
});
</script>

<template>
  <div class="id-statuses">
    <div class="status-editor">
      Add
      <el-input
        v-model="newItemStatus.label"
        draggable
        size="large"
        placeholder="Display Label"
        @keyup.enter="updateStatus"
      >
      </el-input>
      <el-input
        v-model="newItemStatus.code"
        draggable
        size="large"
        placeholder="Code"
        @input="removeSpaces"
        @keyup.enter="updateStatus()"
      >
      </el-input>
    </div>
    <el-button
      class="add"
      :disabled="!((newItemStatus.code || newItemStatus.label) && saveNeeded)"
      @click="updateStatus"
    >
      {{ isEditing ? "Save" : "Add" }}
    </el-button>
    <div class="status-editor save">
      <el-button v-if="isEditing" plain type="danger" @click="resetInputs"
        >Cancel</el-button
      >

      <el-button
        v-if="isEditing && localItemDefinition.statusList.length > 1"
        type="danger"
        @click="deleteStatus"
      >
        Delete
      </el-button>
    </div>

    <SortableStatuses
      :key="localItemDefinition.statusList?.length"
      class="sortable-statuses"
      :status-list="localItemDefinition.statusList"
      @editing-status="editStatus"
    />
  </div>
</template>

<style lang="less">
.id-statuses {
  display: grid;
  grid-template-columns: auto 100px 300px;
  grid-template-areas:
    "statuses statuses statuses"
    "input add save";
  gap: 1em;
  align-items: center;
  user-select: none;

  .status-editor {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .add {
    grid-area: add;
  }
  .save {
    grid-area: save;
  }
  .sortable-statuses {
    grid-area: statuses;
  }
}
</style>
